import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { STRAPI_URL } from '../services/api';

const DEFAULT_SOLUTIONS = [
  {
    id: 'def1',
    text: 'Hizmetler',
    text_en: 'Services',
    description: 'Bulut, üçüncü taraf hizmetleri',
    description_en: 'Cloud and third-party services',
    link: '/hizmetler',
    ikon_url: '/images/solutions/services.png'
  },
  {
    id: 'def2',
    text: 'Personel',
    text_en: 'Personnel',
    description: 'Çalışanlar, yükleniciler',
    description_en: 'Employees and contractors',
    link: '/personel',
    ikon_url: '/images/solutions/personnel.png'
  },
  {
    id: 'def3',
    text: 'Tesisler',
    text_en: 'Facilities',
    description: 'Binalar, veri merkezleri',
    description_en: 'Buildings and data centers',
    link: '/tesisler',
    ikon_url: '/images/solutions/facilities.png'
  },
  {
    id: 'def4',
    text: 'Belgeler',
    text_en: 'Documents',
    description: 'Politikalar, sözleşmeler',
    description_en: 'Policies and contracts',
    link: '/belgeler',
    ikon_url: '/images/solutions/documents.png'
  },
  {
    id: 'def5',
    text: 'Ağ Cihazları',
    text_en: 'Network Devices',
    description: 'Yönlendiriciler, anahtarlar, güvenlik duvarları',
    description_en: 'Routers, switches, and firewalls',
    link: '/ag-cihazlari',
    ikon_url: '/images/solutions/network-devices.png'
  },
  {
    id: 'def6',
    text: 'Donanım',
    text_en: 'Hardware',
    description: 'Sunucular, dizüstü, iş istasyonları',
    description_en: 'Servers, laptops, and workstations',
    link: '/donanim',
    ikon_url: '/images/solutions/hardware.png'
  },
  {
    id: 'def7',
    text: 'Fiziksel Medya',
    text_en: 'Physical Media',
    description: 'USB sürücüler, yedekleme bantları',
    description_en: 'USB drives and backup tapes',
    link: '/fiziksel-medya',
    ikon_url: '/images/solutions/physical-media.png'
  },
  {
    id: 'def8',
    text: 'Veri',
    text_en: 'Data',
    description: 'Veritabanları, dosyalar, bilgi varlıkları',
    description_en: 'Databases, files, and information assets',
    link: '/veri',
    ikon_url: '/images/solutions/data.png'
  },
  {
    id: 'def9',
    text: 'Yazılım',
    text_en: 'Software',
    description: 'Uygulamalar, işletim sistemi araçları',
    description_en: 'Applications, operating system tools',
    link: '/yazilim',
    ikon_url: '/images/solutions/software.png'
  }
];

const routeMap = {
  'Hizmetler': '/hizmetler',
  'Personel': '/personel',
  'Tesisler': '/tesisler',
  'Belgeler': '/belgeler',
  'Ağ Cihazları': '/ag-cihazlari',
  'Ag Cihazlari': '/ag-cihazlari',
  'Donanım': '/donanim',
  'Donanim': '/donanim',
  'Fiziksel Medya': '/fiziksel-medya',
  'Veri': '/veri',
  'Yazılım': '/yazilim',
  'Yazilim': '/yazilim',
};

const Solutions = ({ data }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en' : 'tr';

  // Dil seçici yardımcı fonksiyonu
  const tx = (tr, en) => (lang === 'en' ? (en || tr) : tr);

  // Veri kaynağını belirle
  const items = data?.modules?.length > 0 
    ? data.modules 
    : data?.features?.length > 0 
      ? data.features 
      : DEFAULT_SOLUTIONS;

  /**
   * Görsel URL'sini çözen fonksiyon
   * Strapi v4/v5 yapısını (data.attributes.url) ve düz objeleri destekler
   */
  const getImageUrl = (sol) => {
    // 1. Strapi'den gelen medya alanlarını kontrol et (image veya ikon)
    const mediaField = sol.image || sol.ikon || sol.Image || sol.Ikon;

    if (mediaField) {
      // Strapi v4/v5 Array formatı: media.data[0].attributes.url
      if (Array.isArray(mediaField.data) && mediaField.data.length > 0) {
        const url = mediaField.data[0].attributes?.url;
        if (url) return url.startsWith('http') ? url : `${STRAPI_URL}${url}`;
      }
      
      // Strapi v4 Single object formatı: media.data.attributes.url
      if (mediaField.data?.attributes?.url) {
        const url = mediaField.data.attributes.url;
        return url.startsWith('http') ? url : `${STRAPI_URL}${url}`;
      }

      // Shorthand veya Manuel format: media.url
      if (mediaField.url) {
        return mediaField.url.startsWith('http') ? mediaField.url : `${STRAPI_URL}${mediaField.url}`;
      }
      
      // Strapi doğrudan dizi gönderirse: media[0].url
      if (Array.isArray(mediaField) && mediaField.length > 0) {
        const first = mediaField[0];
        const url = first.url || first.attributes?.url;
        if (url) return url.startsWith('http') ? url : `${STRAPI_URL}${url}`;
      }
    }

    // 2. Eğer API'den veri gelmediyse DEFAULT_SOLUTIONS içindeki yolu döndür
    return sol.ikon_url || null;
  };

  return (
    <section
      className="py-24 px-6 md:px-16 xl:px-32 font-poppins"
      style={{ background: 'linear-gradient(180deg, #68B09F 0%, #114B3D 100%)' }}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 max-w-6xl mx-auto">
        {items.map((sol, index) => {
          const baslik = tx(
            sol.text || sol.Text || sol.baslik,
            sol.text_en || sol.Text_en || sol.baslik_en
          );

          const aciklama = tx(
            sol.description || sol.Description || sol.aciklama,
            sol.description_en || sol.Description_en || sol.aciklama_en
          );

          // Rota belirleme
          const path =
            sol.link ||
            sol.path ||
            routeMap[sol.text] ||
            routeMap[sol.Text] ||
            routeMap[sol.baslik] ||
            '/';

          const imageUrl = getImageUrl(sol);

          return (
            <Link
              key={sol.id || index}
              to={path}
              className="flex flex-col items-start p-10 rounded-2xl bg-white no-underline text-inherit shadow-sm hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 group"
            >
              <div className="w-full flex justify-center mb-6">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={baslik}
                    className="w-24 h-24 object-contain transition-transform duration-300 group-hover:scale-110"
                    loading="lazy"
                    onError={(e) => {
                      // Eğer görsel yüklenirken hata verirse (404), boş görünmemesi için gizle veya placeholder koy
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center">
                    <span className="text-gray-400 text-xs">No Icon</span>
                  </div>
                )}
              </div>

              <h3 className="font-bold text-xl mb-3 text-[#0f1117] group-hover:text-[#00a38d] transition-colors text-left">
                {baslik}
              </h3>

              <p className="text-[#4a6b63] text-sm leading-relaxed opacity-90 max-w-[240px] text-left">
                {aciklama}
              </p>

              <div className="flex items-center gap-1 font-bold text-sm mt-6 text-[#00a38d]">
                <span className="border-b-2 border-transparent group-hover:border-[#00a38d] transition-all">
                  {t('solutions.readMore') || (lang === 'en' ? 'Read More' : 'Devamını oku')}
                </span>
                <svg
                  className="w-4 h-4 transform group-hover:translate-x-1 transition-transform"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

export default Solutions;